import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FolderOpen, Folder, FileText, Image as ImageIcon, Upload, ChevronRight, ChevronDown,
  Search, Building2, Briefcase, X, Download, Loader2, Archive, Users,
  LayoutGrid, List as ListIcon, Star, Camera, FileArchive, CheckSquare, Square, FolderPlus, Pencil, Trash2, FolderUp,
} from "lucide-react";
import { postUpload, postGrande, chiediNome, chiediRinomina, msgErroreFile, ErroreFileBox, NomeDocumentoInline } from "@/components/ConflittoNome";
import { accodaFile, fileDaDrop, soloUtili } from "@/lib/codaCaricamenti";
import { BarraSelezione, SelezionaTutto, eliminaInBlocco, spostaInBlocco, zipInBlocco, usePressioneLunga } from "@/components/SelezioneFile";
import { ProToolbar, RichiestaModal, CondividiModal, StoricoModal, VersioniModal, FileAzioni, CestinoView, StatoBadge } from "@/components/DocumentiPro";

const EXT_IMG = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
function fileExt(name = "") { return name.includes(".") ? name.split(".").pop().toLowerCase() : ""; }
function fmtSize(b) { if (!b) return ""; const k = b / 1024; return k < 1024 ? `${Math.round(k)} KB` : `${(k / 1024).toFixed(1).replace(".", ",")} MB`; }
function fmtDate(iso) { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } }

export default function Documenti() {
  const { isDirezione, isSegreteria } = useAuth();
  const canAgenzia = isDirezione || isSegreteria;
  const [archivio, setArchivio] = useState(false);
  const [tree, setTree] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null); // {type:'pratica'|'agenzia', id}
  const [openTeams, setOpenTeams] = useState({});
  const [openAgents, setOpenAgents] = useState({});

  const loadTree = () => api.get("/documenti/tree", { params: { archivio } }).then((r) => setTree(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { loadTree(); }, [archivio]);

  const ql = q.trim().toLowerCase();
  const filteredTeams = useMemo(() => {
    if (!tree) return [];
    if (!ql) return tree.teams;
    return tree.teams.map((t) => ({
      ...t,
      agenti: t.agenti.map((a) => ({ ...a, pratiche: a.pratiche.filter((p) => (p.label || "").toLowerCase().includes(ql)) }))
        .filter((a) => a.pratiche.length || a.agent.toLowerCase().includes(ql)),
    })).filter((t) => t.agenti.length || t.team.toLowerCase().includes(ql));
  }, [tree, ql]);

  return (
    <div className="fade-up" data-testid="documenti-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold flex items-center gap-2"><FolderOpen className="text-[#FC82ED]" size={24} /> Documenti</h1>
          <p className="text-sm text-[var(--muted)]">Archivio delle pratiche in incarico, per team e agente.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1">
            <button data-testid="doc-tab-incarichi" onClick={() => { setArchivio(false); setSel(null); }} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${!archivio ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}>Incarichi</button>
            <button data-testid="doc-tab-archivio" onClick={() => { setArchivio(true); setSel(null); }} className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${archivio ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}><Archive size={14} /> Archivio</button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-4">
        {/* TREE */}
        <aside className="card-immo p-3 h-max md:sticky md:top-4" data-testid="doc-tree">
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter" && q.trim().length >= 2) setSel({ type: "search", q: q.trim() }); }}
                   placeholder="Cerca pratica… (Invio: cerca ovunque)"
                   className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm outline-none focus:border-[#FC82ED]" />
          </div>
          {q.trim().length >= 2 && (
            <button data-testid="doc-search-global" onClick={() => setSel({ type: "search", q: q.trim() })}
                    className="w-full mb-2 flex items-center gap-2 px-2.5 py-2 rounded-lg bg-[#FC82ED]/10 text-[#111] text-xs font-semibold">
              <Search size={13} /> Cerca "{q.trim()}" in tutti i documenti
            </button>
          )}
          {canAgenzia && (
            <button data-testid="doc-node-agenzia" onClick={() => setSel({ type: "agenzia" })}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-semibold mb-1 ${sel?.type === "agenzia" ? "bg-[#FC82ED]/15 text-[#111]" : "hover:bg-slate-50"}`}>
              <Briefcase size={16} className="text-[#FC82ED]" /> Agenzia
            </button>
          )}
          {!tree ? <div className="skeleton h-40 rounded-lg" /> : filteredTeams.length === 0 ? (
            <p className="text-xs text-[var(--muted)] px-2 py-4">Nessuna pratica {archivio ? "in archivio" : "in incarico"}.</p>
          ) : filteredTeams.map((t) => (
            <div key={t.team} className="mb-1">
              <button onClick={() => setOpenTeams((s) => ({ ...s, [t.team]: !s[t.team] }))}
                      data-testid={`doc-team-${t.team}`} className="w-full flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-bold hover:bg-slate-50">
                {openTeams[t.team] === false ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                <Users size={15} className="text-[#FC82ED]" /> <span className="truncate">{t.team}</span>
              </button>
              {openTeams[t.team] !== false && t.agenti.map((a) => {
                const ak = `${t.team}|${a.agent_id || a.agent}`;
                return (
                  <div key={ak} className="ml-3">
                    <button onClick={() => setOpenAgents((s) => ({ ...s, [ak]: !s[ak] }))}
                            className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      {openAgents[ak] === false ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                      <span className="truncate">{a.agent}</span>
                      <span className="ml-auto text-[10px] text-[var(--muted)]">{a.pratiche.length}</span>
                    </button>
                    {openAgents[ak] !== false && a.pratiche.map((p) => (
                      <button key={p.immobile_id} data-testid={`doc-pratica-${p.immobile_id}`} onClick={() => setSel({ type: "pratica", id: p.immobile_id })}
                              className={`w-full flex items-start gap-2 pl-7 pr-2 py-1.5 rounded-lg text-left text-[13px] ${sel?.id === p.immobile_id ? "bg-[#FC82ED]/15" : "hover:bg-slate-50"}`}>
                        <Building2 size={13} className="mt-0.5 shrink-0 text-slate-400" />
                        <span className="min-w-0"><span className="block truncate">{p.label}</span>
                          <span className="text-[10px] text-[var(--muted)]">{p.n_files} file · {p.avanzamento?.percentuale ?? 0}% approvati</span></span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </aside>

        {/* CONTENT */}
        <section className="min-w-0">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="doc-quick">
            {[["recenti", "Recenti"], ["preferiti", "Preferiti"], ...(canAgenzia ? [["da-approvare", "Da approvare"]] : []), ["scadenza", "In scadenza"], ["cestino", "Cestino"]].map(([k, l]) => (
              <button key={k} data-testid={`quick-${k}`} onClick={() => setSel({ type: "quick", key: k })}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${sel?.type === "quick" && sel.key === k ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "border-[var(--border)] text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          {!sel && <div className="card-immo p-10 text-center text-[var(--muted)]"><FolderOpen size={40} className="mx-auto mb-3 text-[#FC82ED]/50" />Seleziona una pratica dall'albero, usa le sezioni rapide o cerca nei documenti.</div>}
          {sel?.type === "pratica" && <PraticaView immobileId={sel.id} onChanged={loadTree} />}
          {sel?.type === "agenzia" && <AgenziaView canUpload={canAgenzia} />}
          {sel?.type === "quick" && sel.key === "cestino" && <CestinoView />}
          {sel?.type === "quick" && sel.key !== "cestino" && <QuickView sectionKey={sel.key} />}
          {sel?.type === "search" && <SearchView q={sel.q} />}
        </section>
      </div>
    </div>
  );
}

function FileIcon({ name }) {
  return EXT_IMG.includes(fileExt(name)) ? <ImageIcon size={16} className="text-sky-500" /> : <FileText size={16} className="text-[#FC82ED]" />;
}

async function previewBlob(url) {
  const res = await api.get(url, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

export function PraticaView({ immobileId, onChanged, embedded = false }) {
  const { isSegreteria } = useAuth();
  const [p, setP] = useState(null);
  const [modal, setModal] = useState(null);
  const [uploadingTo, setUploadingTo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [view, setView] = useState("list");
  const [sel, setSel] = useState(() => new Set());
  const [fav, setFav] = useState(() => new Set());
  const [dragFolder, setDragFolder] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const scanRef = useRef(null);
  const pendingFolder = useRef(null);
  const dirRef = useRef(null);
  const [appena, setAppena] = useState(null);
  useEffect(() => {
    let h;
    const on = (e) => { if (e.detail?.iid === immobileId) { clearTimeout(h); h = setTimeout(() => { load(); onChanged && onChanged(); }, 800); } };
    window.addEventListener("crm:foto-caricata", on);
    return () => { window.removeEventListener("crm:foto-caricata", on); clearTimeout(h); };
  }, [immobileId]); // eslint-disable-line

  const load = () => api.get(`/documenti/pratica/${immobileId}`).then((r) => setP(r.data)).catch((e) => { if (embedded) setP(false); else toast.error(apiError(e)); });
  useEffect(() => { setP(null); setSel(new Set()); load(); api.get("/documenti/preferiti").then((r) => setFav(new Set((r.data.results || []).map((f) => f.id)))).catch(() => {}); }, [immobileId]);

  const doUpload = async (folder, fileList) => {
    const files = soloUtili(fileList);
    if (!files.length) { toast.error("Nessun file selezionato."); return; }
    const f = files[0];
    if (files.length > 1) {
      const dir = (x) => x.percorso ?? (x.webkitRelativePath || "").split("/").slice(0, -1).join("/");
      const percorsi = [...new Set(files.map(dir).filter(Boolean))];
      const crea = new Set();
      percorsi.forEach((p) => { const parti = [folder, ...p.split("/")].filter(Boolean).slice(0, 5); for (let k = (folder ? 2 : 1); k <= parti.length; k += 1) crea.add(parti.slice(0, k).join("/")); });
      for (const p of [...crea].sort((a, b) => a.split("/").length - b.split("/").length)) await api.post(`/documenti/pratica/${immobileId}/cartelle`, { path: p }).catch(() => {});
      const n = accodaFile(files, { iid: immobileId, sottocartella: folder || "" });
      toast.message(`${n} file in caricamento: puoi continuare a lavorare`);
      return;
    }
    setUploadingTo(folder);
    const kind = EXT_IMG.includes(fileExt(f.name)) && (folder || "Foto").startsWith("Foto") ? "foto" : "documento";
    const fd = new FormData();
    fd.append("file", f); fd.append("entity_type", "immobile"); fd.append("entity_id", immobileId);
    fd.append("kind", kind); if (kind === "documento") fd.append("doc_tipo", "altro");
    if (folder) fd.append("sottocartella", folder);
    try {
      const nuovo = await postUpload(fd);
      toast.success("File caricato");
      if (kind === "documento" && nuovo?.id) setAppena(nuovo.id);
      load(); onChanged && onChanged();
    } catch (err) { if (!err.annullato) toast.error(`${f.name}: ${apiError(err)}`); }
    setUploadingTo(null);
  };
  const triggerUpload = (folder) => { pendingFolder.current = folder; fileRef.current?.click(); };
  const triggerCartella = (folder) => { pendingFolder.current = folder; dirRef.current?.click(); };
  const onPick = (e) => { const files = Array.from(e.target.files || []); e.target.value = ""; doUpload(pendingFolder.current, files); };
  const onScan = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = ""; if (!files.length) return;
    setBusy(true);
    const fd = new FormData(); files.forEach((f) => fd.append("files", f)); fd.append("sottocartella", "Altro");
    try { const r = await api.post(`/documenti/pratica/${immobileId}/scansione`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Scansione creata (${r.data.pagine} pagine)`); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const openFile = async (f) => {
    try { setPreview({ loading: true, nome: f.nome, ext: fileExt(f.nome || f.original_filename) }); const url = await previewBlob(`/files/${f.id}/download`); setPreview({ url, nome: f.nome, ext: fileExt(f.nome || f.original_filename) }); }
    catch (e) { setPreview({ nome: f.nome, errore: await msgErroreFile(e) }); }
  };
  const run = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); onChanged && onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const nuovaCartella = (parent) => { const n = (window.prompt(parent ? `Nuova sottocartella in "${parent}":` : "Nome nuova cartella:") || "").trim(); if (n) run(() => api.post(`/documenti/pratica/${immobileId}/cartelle`, { path: parent ? `${parent}/${n}` : n }), "Cartella creata"); };
  const rinominaCartella = async (fo) => { const n = ((await chiediNome(fo.nome.split("/").pop(), { titolo: "Nuovo nome della cartella", bottone: "Salva", noExt: true })) || "").trim(); if (n) run(() => api.patch(`/documenti/pratica/${immobileId}/cartelle`, { old: fo.nome, new: n }), "Cartella rinominata"); };
  const eliminaCartella = (fo) => { if (window.confirm(`Eliminare la cartella "${fo.nome}" e le sue sottocartelle? I file salgono nella cartella superiore.`)) run(() => api.delete(`/documenti/pratica/${immobileId}/cartelle`, { params: { path: fo.nome } }), "Cartella eliminata"); };
  const sposta = (fileId, dest) => run(() => api.post(`/documenti/pratica/${immobileId}/sposta`, { file_id: fileId, sottocartella: dest }), `Spostato in ${dest}`);
  const rinominaFile = async (f) => { const n = ((await chiediRinomina(f.nome)) || "").trim(); if (n && n !== f.nome) run(() => api.patch(`/files/${f.id}`, { display_name: n }), "File rinominato"); };
  const toggleFav = async (id) => { try { const r = await api.post(`/documenti/preferiti/${id}`); setFav((s) => { const n = new Set(s); r.data.preferito ? n.add(id) : n.delete(id); return n; }); } catch (e) { toast.error(apiError(e)); } };
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tuttiSel = (ids) => setSel((s) => { const n = new Set(s); const on = ids.every((i) => n.has(i)); ids.forEach((i) => (on ? n.delete(i) : n.add(i))); return n; });
  const downloadZip = async () => { setBusy(true); try { const r = await api.get(`/documenti/pratica/${immobileId}/zip`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `${p.label || "pratica"}.zip`; a.click(); URL.revokeObjectURL(u); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };

  if (p === false) return null;
  if (!p) return <div className="skeleton h-64 rounded-xl" />;
  const totFiles = p.folders.reduce((a, f) => a + f.files.length, 0);
  const allFiles = p.folders.flatMap((fo) => fo.files);
  const refresh = () => { load(); onChanged && onChanged(); };
  return (
    <div className="space-y-4" data-testid="pratica-view">
      <input ref={fileRef} type="file" multiple hidden onChange={onPick} data-testid="pratica-file-input" />
      <input ref={dirRef} type="file" multiple webkitdirectory="" directory="" hidden onChange={onPick} data-testid="pratica-cartella-input" />
      <input ref={scanRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={onScan} data-testid="pratica-scan-input" />
      {!embedded && (
        <div className="card-immo p-4">
          <div className="text-[11px] text-[var(--muted)] flex items-center gap-1 mb-1"><span>{p.owner_agent_name || "—"}</span><ChevronRight size={12} /><span className="truncate">{p.label}</span></div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-display text-lg font-bold truncate">{p.label}</h2>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#FC82ED]" style={{ width: `${p.avanzamento?.percentuale ?? 0}%` }} /></div>
              <span className="text-xs text-[var(--muted)]">{p.avanzamento?.percentuale ?? 0}%</span>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex bg-[#F7F7F8] border border-[var(--border)] rounded-lg p-0.5">
            <button data-testid="view-list" onClick={() => setView("list")} className={`p-1.5 rounded ${view === "list" ? "bg-white shadow-sm" : ""}`}><ListIcon size={15} /></button>
            <button data-testid="view-grid" onClick={() => setView("grid")} className={`p-1.5 rounded ${view === "grid" ? "bg-white shadow-sm" : ""}`}><LayoutGrid size={15} /></button>
          </div>
          {sel.size > 0 && <span className="text-xs text-[var(--muted)]">{sel.size} selezionati</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ProToolbar nSel={sel.size} onRichiesta={() => setModal({ t: "richiesta" })} onCondividi={() => setModal({ t: "condividi", files: allFiles.filter((f) => sel.has(f.id)) })} onStorico={() => setModal({ t: "storico" })} />
          <button data-testid="pratica-nuova-cartella-btn" onClick={() => nuovaCartella(null)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40"><FolderPlus size={14} /> Nuova cartella</button>
          <button data-testid="pratica-scan-btn" onClick={() => scanRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40"><Camera size={14} /> Scansiona</button>
          <button data-testid="pratica-zip-btn" onClick={downloadZip} disabled={busy || totFiles === 0} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <FileArchive size={14} />} ZIP pratica</button>
        </div>
      </div>
      <BarraSelezione sel={sel} onAnnulla={() => setSel(new Set())}
        onElimina={() => eliminaInBlocco([...sel], refresh).then((ok) => ok && setSel(new Set()))}
        destinazioni={p.folders.map((x) => x.nome)} onSposta={(dest) => spostaInBlocco(immobileId, [...sel], dest, () => { setSel(new Set()); refresh(); })}
        onZip={() => zipInBlocco([...sel])} />
      {p.checklist && (
        <div className="card-immo p-3 flex flex-wrap gap-1.5 items-center" data-testid="pratica-checklist">
          <span className="text-xs font-semibold mr-1">Checklist:</span>
          {p.checklist.map((c) => <span key={c.doc_tipo} className="text-[11px] inline-flex items-center gap-1">{c.label}<StatoBadge s={c} testid={`checklist-${c.doc_tipo}`} /></span>)}
        </div>
      )}
      {p.folders.map((fo) => (
        <div key={fo.nome} style={{ marginLeft: `${(fo.livello || 0) * 20}px` }} className={`card-immo p-4 ${dragFolder === fo.nome ? "ring-2 ring-[#FC82ED]" : ""}`} data-testid={`folder-${fo.nome}`}
             onDragOver={(e) => { e.preventDefault(); setDragFolder(fo.nome); }} onDragLeave={() => setDragFolder(null)}
             onDrop={(e) => { e.preventDefault(); setDragFolder(null); const fid = e.dataTransfer.getData("text/crm-file"); if (fid) sposta(fid, fo.nome); else fileDaDrop(e.dataTransfer).then((l) => doUpload(fo.nome, l)); }}>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="flex items-center gap-2 font-semibold text-sm min-w-0"><Folder size={16} className="text-[#FC82ED] shrink-0" /> <span className="truncate">{fo.nome.split("/").pop()}</span> <span className="text-[11px] text-[var(--muted)] font-normal">({fo.files.length})</span>
              {fo.files.length > 0 && <SelezionaTutto ids={fo.files.map((x) => x.id)} sel={sel} onTutti={tuttiSel} testid={`folder-seltutto-${fo.nome}`} />}</div>
            <div className="flex items-center gap-1.5 flex-wrap">
            <button data-testid={`folder-sub-${fo.nome}`} onClick={() => nuovaCartella(fo.nome)} title="Nuova sottocartella" className="p-1.5 rounded-lg bg-white border border-[var(--border)] hover:border-[#FC82ED]/40"><FolderPlus size={13} /></button>
            {!fo.standard && <button data-testid={`folder-rename-${fo.nome}`} onClick={() => rinominaCartella(fo)} title="Rinomina cartella" className="p-1.5 rounded-lg bg-white border border-[var(--border)] hover:border-[#FC82ED]/40"><Pencil size={13} /></button>}
            {!fo.standard && <button data-testid={`folder-delete-${fo.nome}`} onClick={() => eliminaCartella(fo)} title="Elimina cartella" className="p-1.5 rounded-lg bg-white border border-[var(--border)] text-red-500 hover:border-red-300"><Trash2 size={13} /></button>}
            {fo.files.length > 0 && <button data-testid={`folder-share-${fo.nome}`} onClick={() => setModal({ t: "condividi", files: fo.files })} className="px-2.5 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40">Condividi cartella</button>}
            <button data-testid={`folder-upload-${fo.nome}`} onClick={() => triggerUpload(fo.nome)} disabled={uploadingTo === fo.nome}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40">
              {uploadingTo === fo.nome ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Carica
            </button>
            <button data-testid={`folder-upload-dir-${fo.nome}`} onClick={() => triggerCartella(fo.nome)} title="Carica un'intera cartella del computer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40">
              <FolderUp size={13} /> Carica cartella
            </button>
            </div>
          </div>
          {fo.files.length === 0 ? <p className="text-xs text-[var(--muted)] py-1">Trascina qui i file o usa "Carica".</p> : view === "grid" ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {fo.files.map((f) => (
                <FileTile key={f.id} id={f.id} onLong={() => toggleSel(f.id)} className={`relative rounded-xl border p-3 text-center cursor-pointer hover:border-[#FC82ED]/40 ${sel.has(f.id) ? "border-[#FC82ED] bg-[#FC82ED]/5" : "border-[var(--border)]"}`} onClick={() => (sel.size ? toggleSel(f.id) : openFile(f))}>
                  <button data-testid={`file-sel-${f.id}`} onClick={(e) => { e.stopPropagation(); toggleSel(f.id); }} className="absolute top-1.5 left-1.5">{sel.has(f.id) ? <CheckSquare size={15} className="text-[#FC82ED]" /> : <Square size={15} className="text-slate-300" />}</button>
                  <button onClick={(e) => { e.stopPropagation(); toggleFav(f.id); }} className="absolute top-1.5 right-1.5"><Star size={15} className={fav.has(f.id) ? "fill-amber-400 text-amber-400" : "text-slate-300"} /></button>
                  <div className="py-3 flex justify-center"><FileIcon name={f.nome || f.original_filename} /></div>
                  <div className="text-[11px] truncate">{f.nome}</div>
                </FileTile>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {fo.files.map((f) => (
                <div key={f.id} data-testid={`file-${f.id}`} draggable onDragStart={(e) => e.dataTransfer.setData("text/crm-file", f.id)} className={`flex items-center gap-2 py-2 flex-wrap ${sel.has(f.id) ? "bg-[#FC82ED]/5" : ""}`}>
                  <button data-testid={`file-sel-${f.id}`} onClick={() => toggleSel(f.id)}>{sel.has(f.id) ? <CheckSquare size={15} className="text-[#FC82ED]" /> : <Square size={15} className="text-slate-300" />}</button>
                  <button onClick={() => openFile(f)} className="flex items-center gap-3 flex-1 text-left hover:bg-slate-50 rounded-lg px-1 min-w-0">
                    <FileIcon name={f.nome || f.original_filename} />
                    <span className="min-w-0 flex-1"><span className="block text-sm truncate">{f.nome}</span>
                      <span className="text-[11px] text-[var(--muted)]">{fmtSize(f.size)} · {f.uploaded_by_name || "—"} · {fmtDate(f.created_at)}</span></span>
                  </button>
                  <FileAzioni f={f} immobileId={immobileId} isSegreteria={isSegreteria} onDone={refresh} onVersioni={(x) => setModal({ t: "versioni", file: x })} />
                  {appena === f.id && <div className="basis-full pl-7"><NomeDocumentoInline valore={f.nome} testid={`nome-documento-${f.id}`} onSalva={(n) => run(() => api.patch(`/files/${f.id}`, { display_name: n }), "Nome aggiornato")} /></div>}
                  <button data-testid={`file-rinomina-${f.id}`} onClick={() => rinominaFile(f)} title="Rinomina" className="p-1 rounded text-slate-400 hover:text-[#111]"><Pencil size={13} /></button>
                  <select data-testid={`file-sposta-${f.id}`} value="" onChange={(e) => e.target.value && sposta(f.id, e.target.value)} title="Sposta in…"
                    className="text-[11px] max-w-[120px] px-1.5 py-1 rounded-lg border border-[var(--border)] bg-white">
                    <option value="">Sposta in…</option>
                    {p.folders.filter((x) => x.nome !== fo.nome).map((x) => <option key={x.nome} value={x.nome}>{x.nome}</option>)}
                  </select>
                  <button onClick={() => toggleFav(f.id)}><Star size={15} className={fav.has(f.id) ? "fill-amber-400 text-amber-400" : "text-slate-300"} /></button>
                  <Download size={14} className="text-[var(--muted)] shrink-0 cursor-pointer" onClick={() => openFile(f)} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {preview && <PreviewModal p={preview} onClose={() => setPreview(null)} />}
      {modal?.t === "richiesta" && <RichiestaModal immobileId={immobileId} checklist={p.checklist || []} onClose={() => setModal(null)} />}
      {modal?.t === "condividi" && <CondividiModal immobileId={immobileId} files={modal.files} onClose={() => setModal(null)} />}
      {modal?.t === "storico" && <StoricoModal immobileId={immobileId} onClose={() => setModal(null)} />}
      {modal?.t === "versioni" && <VersioniModal file={modal.file} onDone={refresh} onClose={() => setModal(null)} />}
    </div>
  );
}

function ResultList({ items, empty }) {
  const [preview, setPreview] = useState(null);
  const open = async (f) => {
    const url0 = f.entity_type === "agenzia" ? `/documenti/agenzia/${f.id}/download` : `/files/${f.id}/download`;
    try { setPreview({ loading: true, nome: f.nome, ext: fileExt(f.nome) }); const url = await previewBlob(url0); setPreview({ url, nome: f.nome, ext: fileExt(f.nome) }); }
    catch (e) { setPreview({ nome: f.nome, errore: await msgErroreFile(e) }); }
  };
  if (!items) return <div className="skeleton h-40 rounded-xl" />;
  if (!items.length) return <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">{empty}</div>;
  return (
    <div className="card-immo p-2 divide-y divide-slate-100" data-testid="result-list">
      {items.map((f) => (
        <button key={f.id} data-testid={`result-${f.id}`} onClick={() => open(f)} className="w-full flex items-center gap-3 py-2.5 px-2 text-left hover:bg-slate-50 rounded-lg">
          <FileIcon name={f.nome} />
          <span className="min-w-0 flex-1"><span className="block text-sm truncate">{f.nome}</span>
            <span className="text-[11px] text-[var(--muted)] truncate">{f.pratica_label || "—"}{f.sottocartella ? ` · ${f.sottocartella}` : ""} · {fmtDate(f.created_at)}</span></span>
          <Download size={14} className="text-[var(--muted)] shrink-0" />
        </button>
      ))}
      {preview && <PreviewModal p={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

const QUICK_TITLES = { recenti: "Recenti", preferiti: "Preferiti", "da-approvare": "Da approvare", scadenza: "In scadenza" };
function QuickView({ sectionKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (sectionKey === "da-approvare") { api.get("/documenti/da-approvare").then((r) => setData({ pratiche: r.data.results })).catch(() => setData({ pratiche: [] })); }
    else if (sectionKey === "scadenza") { api.get("/documenti/scadenza", { params: { days: 30 } }).then((r) => setData({ items: (r.data || []).map((d) => ({ id: d.id, nome: d.original_filename, pratica_label: d.titolo, sottocartella: d.doc_tipo, created_at: d.scadenza, entity_type: "immobile" })) })).catch(() => setData({ items: [] })); }
    else api.get(`/documenti/${sectionKey}`).then((r) => setData({ items: r.data.results })).catch(() => setData({ items: [] }));
  }, [sectionKey]);
  return (
    <div data-testid={`quickview-${sectionKey}`}>
      <h2 className="font-display text-lg font-bold mb-3">{QUICK_TITLES[sectionKey]}</h2>
      {sectionKey === "da-approvare" ? (
        !data ? <div className="skeleton h-40 rounded-xl" /> : !data.pratiche?.length ? <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">Nessun documento in attesa di approvazione.</div> : (
          <div className="card-immo p-2 divide-y divide-slate-100">
            {data.pratiche.map((p) => (
              <div key={p.immobile_id} data-testid={`approve-${p.immobile_id}`} className="flex items-center justify-between gap-2 py-2.5 px-2">
                <span className="min-w-0"><span className="block text-sm truncate">{p.label}</span><span className="text-[11px] text-[var(--muted)]">{p.owner_agent_name}</span></span>
                <span className="text-xs font-bold text-amber-600 shrink-0">{p.da_approvare} da approvare</span>
              </div>
            ))}
          </div>
        )
      ) : <ResultList items={data?.items} empty="Nessun documento." />}
    </div>
  );
}

function SearchView({ q }) {
  const [data, setData] = useState(null);
  const [tipo, setTipo] = useState("");
  useEffect(() => { setData(null); api.get("/documenti/cerca", { params: { q, tipo: tipo || undefined } }).then((r) => setData(r.data.results)).catch(() => setData([])); }, [q, tipo]);
  return (
    <div data-testid="searchview">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="font-display text-lg font-bold">Risultati per "{q}"</h2>
        <select data-testid="search-filter-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm">
          <option value="">Tutti i tipi</option>
          <option value="visura">Visura catastale</option><option value="visura_ipotecaria">Visura ipotecaria</option><option value="are">ARE</option><option value="ape">APE</option><option value="planimetria">Planimetria</option>
          <option value="incarico">Incarico</option><option value="documento_identita">Documento identità</option><option value="altro">Altro</option>
        </select>
      </div>
      <ResultList items={data} empty="Nessun documento trovato (ricerca anche nel testo dei PDF)." />
    </div>
  );
}

function AgenziaView({ canUpload }) {
  const [data, setData] = useState(null);
  const [uploadingTo, setUploadingTo] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const pendingFolder = useRef(null);
  const load = () => api.get("/documenti/agenzia").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const triggerUpload = (folder) => { pendingFolder.current = folder; fileRef.current?.click(); };
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = ""; if (!files.length) return;
    setUploadingTo(pendingFolder.current);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f); fd.append("sottocartella", pendingFolder.current);
      try { await postGrande("/documenti/agenzia/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(`${f.name}: ${apiError(err)}`); }
    }
    setUploadingTo(null); toast.success("Caricamento completato"); load();
  };
  const openFile = async (f) => {
    try { setPreview({ loading: true, nome: f.nome, ext: fileExt(f.nome) }); const url = await previewBlob(`/documenti/agenzia/${f.id}/download`); setPreview({ url, nome: f.nome, ext: fileExt(f.nome) }); }
    catch (e) { setPreview({ nome: f.nome, errore: await msgErroreFile(e) }); }
  };
  if (!data) return <div className="skeleton h-64 rounded-xl" />;
  return (
    <div className="space-y-4" data-testid="agenzia-view">
      <input ref={fileRef} type="file" multiple hidden onChange={onPick} />
      <div className="card-immo p-4"><h2 className="font-display text-lg font-bold flex items-center gap-2"><Briefcase size={18} className="text-[#FC82ED]" /> Agenzia — documenti generali</h2>
        <p className="text-xs text-[var(--muted)]">Modulistica, contratti tipo, procedure e formazione. {canUpload ? "Puoi caricare e aggiornare i file." : "Leggibile da tutti."}</p></div>
      {data.folders.map((fo) => (
        <div key={fo.nome} className="card-immo p-4" data-testid={`agenzia-folder-${fo.nome}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 font-semibold text-sm"><Folder size={16} className="text-[#FC82ED]" /> {fo.nome} <span className="text-[11px] text-[var(--muted)] font-normal">({fo.files.length})</span></div>
            {canUpload && <button data-testid={`agenzia-upload-${fo.nome}`} onClick={() => triggerUpload(fo.nome)} disabled={uploadingTo === fo.nome}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40">
              {uploadingTo === fo.nome ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Carica</button>}
          </div>
          {fo.files.length === 0 ? <p className="text-xs text-[var(--muted)] py-1">Vuota.</p> : (
            <div className="divide-y divide-slate-100">{fo.files.map((f) => (
              <button key={f.id} onClick={() => openFile(f)} className="w-full flex items-center gap-3 py-2 text-left hover:bg-slate-50 rounded-lg px-1">
                <FileIcon name={f.nome} /><span className="min-w-0 flex-1"><span className="block text-sm truncate">{f.nome}</span>
                  <span className="text-[11px] text-[var(--muted)]">{fmtSize(f.size)} · {f.uploaded_by_name || "—"} · {fmtDate(f.created_at)}</span></span>
                <Download size={14} className="text-[var(--muted)] shrink-0" /></button>
            ))}</div>
          )}
        </div>
      ))}
      {preview && <PreviewModal p={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function FileTile({ id, onLong, children, ...rest }) {
  const lp = usePressioneLunga(onLong);
  return <div data-testid={`file-${id}`} {...lp} {...rest}>{children}</div>;
}

function PreviewModal({ p, onClose }) {
  const isImg = EXT_IMG.includes(p.ext);
  const [imgErr, setImgErr] = useState(null);
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b border-[var(--border)]">
          <span className="font-semibold text-sm truncate">{p.nome}</span>
          <div className="flex items-center gap-2">
            {p.url && <a href={p.url} download={p.nome} className="p-1.5 rounded-lg hover:bg-slate-100" title="Scarica"><Download size={16} /></a>}
            <button onClick={onClose} data-testid="preview-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center min-h-[300px]">
          {p.errore || imgErr ? <ErroreFileBox msg={p.errore || imgErr} onClose={onClose} /> :
            p.loading ? <Loader2 className="animate-spin text-[#FC82ED]" /> :
            isImg ? <img src={p.url} alt={p.nome} onError={() => setImgErr("Impossibile mostrare l'immagine: il file potrebbe essere danneggiato.")} className="max-w-full max-h-[80vh] object-contain" /> :
            p.ext === "pdf" ? <iframe src={p.url} title={p.nome} className="w-full h-[80vh]" /> :
            <div className="text-center p-8 text-sm text-[var(--muted)]"><FileText size={40} className="mx-auto mb-3 text-[#FC82ED]/50" />Anteprima non disponibile per questo formato.<br /><a href={p.url} download={p.nome} className="text-[#FC82ED] font-semibold">Scarica il file</a></div>}
        </div>
      </div>
    </div>,
    document.body
  );
}
